(function () {
  'use strict';

  function initLocalPlaces(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const nodes = scope.querySelectorAll('[data-local-places]');
    if (!nodes.length) return;

    nodes.forEach(function (node) {
      if (node.dataset.localPlacesInitialized === 'true') return;
      node.dataset.localPlacesInitialized = 'true';
      setupNode(node);
    });
  }

  function initLocalPlacesLazy(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const nodes = scope.querySelectorAll('[data-local-places]');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      initLocalPlaces(scope);
      return;
    }

    const observer =
      window.__localPlacesObserver ||
      (window.__localPlacesObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            const node = entry.target;
            try {
              observer.unobserve(node);
            } catch (e) {}
            if (!node || node.dataset.localPlacesInitialized === 'true') return;
            node.dataset.localPlacesInitialized = 'true';
            setupNode(node);
          });
        },
        { root: null, rootMargin: '600px 0px', threshold: 0 }
      ));

    nodes.forEach(function (node) {
      if (node.dataset.localPlacesInitialized === 'true') return;
      try {
        observer.observe(node);
      } catch (e) {
        // If observation fails for any reason, fallback to eager init.
        node.dataset.localPlacesInitialized = 'true';
        setupNode(node);
      }
    });
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getNum(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function createErrorHtml(message) {
    return '<div class="local-places-error">' + escapeHtml(message) + '</div>';
  }

  const i18nDefaults = {
    unavailable: 'Local results are unavailable.',
    loading: 'Loading…',
    loadError: 'Could not load local results.',
    searchCenter: 'Search center',
    zipPrefix: 'ZIP ',
    noPlacesFound: 'No places found for this area.',
    noResultsFound: 'No results found.',
    geocoderUnavailable: 'Geocoder unavailable'
  };
  const i18n = Object.assign({}, i18nDefaults, (window.theme_i18n && window.theme_i18n.localPlaces) || {});

  function buildDirectionsUrl(placeId, fallbackQuery) {
    const base = 'https://www.google.com/maps/search/?api=1';
    if (placeId) {
      const q = fallbackQuery ? '&query=' + encodeURIComponent(fallbackQuery) : '';
      return base + q + '&query_place_id=' + encodeURIComponent(placeId);
    }
    if (fallbackQuery) return base + '&query=' + encodeURIComponent(fallbackQuery);
    return base;
  }

  function sanitizeGoogleMapsApiKey(raw) {
    let s = String(raw || '').trim();
    if (!s) return '';
    const fromQuery = s.match(/[?&]key=([^&\s#]+)/);
    if (fromQuery) return fromQuery[1].trim();
    const amp = s.indexOf('&');
    if (amp !== -1) s = s.slice(0, amp).trim();
    const q = s.indexOf('?');
    if (q !== -1) s = s.slice(0, q).trim();
    const m = s.match(/AIza[0-9A-Za-z_-]{30,}/);
    if (m) return m[0];
    return s;
  }

  function searchKeywordFor(placeType) {
    const kw =
      {
        school: 'schools',
        hospital: 'hospitals',
        funeral_home: 'funeral homes'
      }[placeType] || placeType;
    return kw;
  }

  function searchAttemptsFor(placeType, locationQuery) {
    const kw = searchKeywordFor(placeType);
    return [
      { query: kw + ' near ' + locationQuery },
      { query: kw + ' in ' + locationQuery },
      { query: locationQuery, type: placeType },
      { query: kw + ' ' + locationQuery }
    ];
  }

  function textSearchOnce(service, request) {
    return new Promise(function (resolve) {
      service.textSearch(request, function (results, status) {
        resolve({ results: results || [], status: status });
      });
    });
  }

  function geocodeOnce(locationQuery) {
    return new Promise(function (resolve, reject) {
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        reject(new Error(i18n.geocoderUnavailable));
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: locationQuery }, function (results, status) {
        if (status === 'OK' && results && results[0] && results[0].geometry && results[0].geometry.location) {
          resolve(results[0].geometry.location);
          return;
        }
        reject(new Error('Geocode: ' + status));
      });
    });
  }

  function toRadians(deg) {
    return (deg * Math.PI) / 180;
  }

  function milesBetween(lat1, lng1, lat2, lng2) {
    const R = 3958.7613; // miles
    const dLat = toRadians(lat2 - lat1);
    const dLng = toRadians(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function normalizeMaybeJsonArray(value) {
    const s = String(value || '').trim();
    if (!s) return '';
    if (s[0] !== '[') return s;
    try {
      const arr = JSON.parse(s);
      if (Array.isArray(arr) && arr.length) return String(arr[0] || '').trim();
    } catch (e) {}
    return s;
  }

  function getLat(loc) {
    if (!loc) return null;
    const v = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
    return Number.isFinite(v) ? v : null;
  }

  function getLng(loc) {
    if (!loc) return null;
    const v = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
    return Number.isFinite(v) ? v : null;
  }

  function distanceMilesFrom(centerLoc, placeLoc) {
    const fromLat = getLat(centerLoc);
    const fromLng = getLng(centerLoc);
    const toLat = getLat(placeLoc);
    const toLng = getLng(placeLoc);
    if (fromLat == null || fromLng == null || toLat == null || toLng == null) return null;
    return milesBetween(fromLat, fromLng, toLat, toLng);
  }

  function parseBool(value) {
    return String(value || '').trim() === 'true';
  }

  function createMap(container, centerLoc, places, centerLabel, mapHeightPx) {
    if (!container || !centerLoc || !window.google || !window.google.maps) return null;

    const mapEl = document.createElement('div');
    mapEl.className = 'local-places-map';
    mapEl.style.height = String(mapHeightPx || 260) + 'px';
    container.insertBefore(mapEl, container.firstChild);

    const center = {
      lat: getLat(centerLoc) || 0,
      lng: getLng(centerLoc) || 0
    };

    const map = new window.google.maps.Map(mapEl, {
      center: center,
      zoom: 12,
      clickableIcons: false,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false
    });

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(center);

    new window.google.maps.Marker({
      map: map,
      position: center,
      title: centerLabel || i18n.searchCenter,
      label: { text: '●', color: '#825df9', fontSize: '14px' }
    });

    (places || []).forEach(function (p) {
      const loc = p && p.geometry && p.geometry.location;
      const lat = getLat(loc);
      const lng = getLng(loc);
      if (lat == null || lng == null) return;
      const pos = { lat: lat, lng: lng };
      bounds.extend(pos);
      new window.google.maps.Marker({
        map: map,
        position: pos,
        title: p.name || ''
      });
    });

    if (places && places.length) {
      map.fitBounds(bounds, 28);
    }

    return map;
  }

  function runTextSearchWithFallbacks(service, placeType, locationQuery, radiusMiles, centerLoc) {
    const hasRadius = Number(radiusMiles) > 0;
    const radiusMeters = hasRadius ? Math.max(0, Math.round(Number(radiusMiles) * 1609.34)) : 0;
    const kw = searchKeywordFor(placeType);

    // When radius is "off", still bias near the page location (about 1 mile).
    const biasRadiusMeters = hasRadius ? radiusMeters : 1609;

    const baseAttempts = searchAttemptsFor(placeType, locationQuery);
    const attempts = centerLoc
      ? [
          { query: kw, location: centerLoc, radius: biasRadiusMeters },
          { query: kw + ' in ' + locationQuery, location: centerLoc, radius: biasRadiusMeters }
        ].concat(baseAttempts)
      : baseAttempts;

    return Promise.resolve(attempts).then(function (attemptsList) {
    let i = 0;

    function next() {
      if (i >= attemptsList.length) {
        return Promise.reject(new Error(i18n.noPlacesFound));
      }
      const req = attemptsList[i];
      i += 1;
      return textSearchOnce(service, req).then(function (out) {
        const st = out.status;
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        const ZERO = window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
        if (st === OK && out.results.length) {
          return out.results;
        }
        if (st === ZERO || st === OK) {
          return next();
        }
        if (
          st === window.google.maps.places.PlacesServiceStatus.REQUEST_DENIED ||
          st === window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT
        ) {
          return Promise.reject(new Error('Places: ' + st));
        }
        return next();
      });
    }

    return next();
    });
  }

  function loadGooglePlaces(apiKey) {
    return new Promise(function (resolve, reject) {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve(window.google.maps.places);
        return;
      }

      if (window.__localPlacesGoogleLoadingPromise) {
        window.__localPlacesGoogleLoadingPromise.then(resolve).catch(reject);
        return;
      }

      window.__localPlacesGoogleLoadingPromise = new Promise(function (res, rej) {
        var cbName = '__localPlacesGoogleInit';
        window[cbName] = function () {
          try {
            if (window.google && window.google.maps && window.google.maps.places) {
              res(window.google.maps.places);
            } else {
              rej(new Error('Google Places JS did not initialize.'));
            }
          } finally {
            try {
              delete window[cbName];
            } catch (e) {
              window[cbName] = undefined;
            }
          }
        };

        const script = document.createElement('script');
        script.src =
          'https://maps.googleapis.com/maps/api/js?key=' +
          encodeURIComponent(apiKey) +
          '&libraries=places&loading=async&callback=' +
          cbName;
        script.async = true;
        script.defer = true;

        script.onerror = function () {
          try {
            delete window[cbName];
          } catch (e) {}
          rej(new Error('Failed to load Google Maps JS.'));
        };

        document.head.appendChild(script);
      });

      window.__localPlacesGoogleLoadingPromise.then(resolve).catch(reject);
    });
  }

  function setupNode(node) {
      const apiKey = sanitizeGoogleMapsApiKey(node.dataset.apiKey || '');
      const placeType = node.dataset.placeType || '';
      const locationQuery = node.dataset.locationQuery || '';
      const zip = normalizeMaybeJsonArray(node.dataset.zip);
      const centerLabel = normalizeMaybeJsonArray(node.dataset.centerLabel);
      const limit = getNum(node.dataset.limit, 5);
      const radiusMiles = getNum(node.dataset.radiusMiles, 0);
      const showMap = parseBool(node.dataset.showMap);
      const mapHeight = getNum(node.dataset.mapHeight, 260);

      const listEl = document.createElement('ul');
      listEl.className = 'local-places-list';
      node.innerHTML = '';
      node.appendChild(listEl);

      if (!apiKey || !placeType || !locationQuery) {
        node.innerHTML = createErrorHtml(i18n.unavailable);
        return;
      }

      const loadingEl = document.createElement('div');
      loadingEl.className = 'local-places-loading';
      loadingEl.textContent = i18n.loading;
      node.insertBefore(loadingEl, listEl);

      loadGooglePlaces(apiKey)
        .then(function () {
          const service = new window.google.maps.places.PlacesService(document.createElement('div'));
          // Prefer town/state over ZIP for search accuracy.
          const centerQuery = locationQuery;
          return geocodeOnce(centerQuery)
            .catch(function () {
              return null;
            })
            .then(function (centerLoc) {
              return runTextSearchWithFallbacks(service, placeType, centerQuery, radiusMiles, centerLoc).then(function (results) {
                return { service: service, results: results, centerLoc: centerLoc };
              });
            });
        })
        .then(function (payload) {
          const service = payload.service;
          const results = payload.results;
          const centerLoc = payload.centerLoc;
          let withIds = (results || []).filter(function (p) {
            return p && p.place_id;
          });

          // Enforce radius strictly (straight-line distance from the geocoded center).
          if (radiusMiles > 0 && centerLoc) {
            withIds = withIds.filter(function (p) {
              const d = distanceMilesFrom(centerLoc, p && p.geometry && p.geometry.location);
              if (d == null) return false;
              return d <= radiusMiles + 0.2;
            });
          }

          const top = withIds.slice(0, limit);
          if (!top.length) {
            throw new Error(i18n.noResultsFound);
          }

          listEl.innerHTML = '';
          const listItems = [];

          if (showMap && centerLoc) {
            createMap(node, centerLoc, top, centerLabel || (zip ? i18n.zipPrefix + zip : locationQuery), mapHeight);
          }

          top.forEach(function (place) {
            const li = document.createElement('li');
            li.className = 'local-places-item';
            li.innerHTML =
              '<div class="local-places-name"></div>' +
              '<div class="local-places-distance"></div>' +
              '<div class="local-places-address"></div>' +
              '<div class="local-places-directions"></div>' +
              '<div class="local-places-rating"></div>' +
              '<div class="local-places-phone"></div>';

            li.querySelector('.local-places-name').textContent = place.name || '';
            const distEl = li.querySelector('.local-places-distance');
            if (distEl && centerLoc && place && place.geometry && place.geometry.location) {
              const miles = distanceMilesFrom(centerLoc, place.geometry.location);
              if (miles != null) {
                distEl.textContent = miles.toFixed(miles < 10 ? 1 : 0) + ' mi away';
              }
            } else if (distEl) {
              distEl.textContent = '';
            }
            const addr = place.formatted_address || place.vicinity || '';
            li.querySelector('.local-places-address').textContent = addr;

            const directionsEl = li.querySelector('.local-places-directions');
            if (directionsEl) {
              const q = [place.name || '', addr || ''].filter(Boolean).join(' ');
              const url = buildDirectionsUrl(place.place_id, q);
              directionsEl.innerHTML = '<a target="_blank" rel="noopener" href="' + escapeHtml(url) + '">Directions</a>';
            }

            listEl.appendChild(li);
            listItems.push({ el: li, placeId: place.place_id });
          });

          const detailFields = [
            'name',
            'formatted_address',
            'formatted_phone_number',
            'international_phone_number',
            'rating'
          ];

          return Promise.all(
            listItems.map(function (item) {
              return new Promise(function (res) {
                service.getDetails(
                  {
                    placeId: item.placeId,
                    fields: detailFields
                  },
                  function (place, detailStatus) {
                    if (detailStatus !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
                      res(null);
                      return;
                    }
                    res(place);
                  }
                );
              });
            })
          ).then(function (results) {
            results.forEach(function (value, idx) {
              if (!value) return;
              const target = listItems[idx];
              if (!target) return;

              const ratingEl = target.el.querySelector('.local-places-rating');
              if (value.rating) ratingEl.textContent = '★ ' + String(value.rating);
              const phoneEl = target.el.querySelector('.local-places-phone');
              const phone = value.formatted_phone_number || value.international_phone_number || '';
              if (phone) {
                phoneEl.innerHTML = '<a href="tel:' + escapeHtml(phone) + '">' + escapeHtml(phone) + '</a>';
              }

              if (value.formatted_address) {
                target.el.querySelector('.local-places-address').textContent = value.formatted_address;
              }
            });
          });
        })
        .then(function () {
          if (loadingEl && loadingEl.parentNode) loadingEl.parentNode.removeChild(loadingEl);
        })
        .catch(function (err) {
          try {
            if (loadingEl && loadingEl.parentNode) loadingEl.parentNode.removeChild(loadingEl);
          } catch (e) {}
          node.innerHTML = createErrorHtml(i18n.loadError + (err && err.message ? ' ' + err.message : ''));
        });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initLocalPlacesLazy(document);
    });
  } else {
    initLocalPlacesLazy(document);
  }

  // Theme editor support (dynamic section reloads)
  document.addEventListener('shopify:section:load', function (evt) {
    try {
      const sectionRoot = evt && evt.target ? evt.target : document;
      initLocalPlacesLazy(sectionRoot);
    } catch (e) {}
  });
})();
